/**
 * Native session credential store (Capacitor builds).
 *
 * WHY THIS EXISTS. The web app authenticates with an httpOnly `session_id`
 * cookie. Native cannot: a bundled build is cross-site (webview origin
 * `https://localhost` / `capacitor://localhost` → API `https://gotradeloop.com`),
 * and Capacitor's iOS cookie layer mangles `Set-Cookie` headers outright — it
 * splits the header on commas, shredding any cookie carrying a date
 * (`src/docs/GOOGLE_OAUTH_SETUP.md` §8.12; upstream ionic-team/capacitor#6280,
 * unfixed since 2023).
 *
 * WHAT CHANGES, AND WHAT DOES NOT. The credential is the **same opaque
 * `user_sessions.id`** the cookie would have carried — not a JWT, not a Supabase
 * token. Only its envelope differs: the server returns it in `X-Session-Id`, we
 * keep it in the Keychain (iOS) / Keystore (Android), and send it back as
 * `Authorization: Bearer <id>`. The backend routes it to the same
 * `resolveSession` path, so revocation, the session cache and the
 * `session_valid_after` check are untouched, and token custody stays on the
 * server.
 *
 * WHY `@aparajita/capacitor-secure-storage` AND NOT `capacitor-native-biometric`.
 * The latter ships a CocoaPods podspec but **no `Package.swift`**, and this app
 * is SPM (`ios/App/CapApp-SPM`, no Podfile). `npx cap sync` warns
 * "capacitor-native-biometric does not have a Package.swift" and then simply
 * omits it, so on iOS the plugin was never in the binary and every call rejected
 * as unimplemented. Android was fine, which is why it looked like an iOS
 * Keychain bug rather than a missing plugin.
 */

import { SecureStorage, StorageError, StorageErrorType } from '@aparajita/capacitor-secure-storage';
import { isNative } from '@/utils/platform';

/**
 * MUST NOT collide with keys used by biometric credential storage. The prior
 * plugin keyed by `server` and reusing its value would have wiped the user's
 * saved biometric login; the same care applies here.
 */
const SESSION_KEY = 'tradeloop.session.id';

/**
 * The outcome of a restore attempt. These are four genuinely different states
 * and collapsing them is what let a missing native plugin masquerade as "the
 * user was simply logged out" — the failure that cost a full device round-trip
 * to diagnose. `unavailable` in particular means the app is misconfigured, not
 * that the user needs to sign in.
 */
export type RestoreResult =
  | { status: 'restored'; sessionId: string }
  | { status: 'missing' }
  | { status: 'unavailable'; error: Error }
  | { status: 'corrupt'; error: Error };

/**
 * Read-through cache. Every outgoing native request needs the id and the request
 * path (`nativeApiOrigin`) is synchronous, so it cannot await the Keychain.
 * `restoreSessionId()` populates this once at startup.
 */
let cachedSessionId: string | null = null;

/**
 * Set when the secure store itself is broken (plugin missing, OS error) — as
 * opposed to simply holding no session. Surfaced in the UI because a native
 * store failure is an integration bug the user cannot otherwise see, and
 * silently treating it as "logged out" is what hid the last one.
 */
let storeFailure: string | null = null;

export const getStoreFailure = (): string | null => storeFailure;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

/** The current session id, or null. Synchronous — safe to call per request. */
export const getSessionId = (): string | null => cachedSessionId;

/**
 * Load any persisted session id into the cache. Call once before the first
 * authenticated request, so a returning user stays signed in.
 *
 * Note `SecureStorage.get()` resolves `null` for a key that is genuinely absent
 * and *throws* `StorageError` for a real failure, so the two are distinguishable
 * here without inspecting messages.
 */
export const restoreSessionId = async (): Promise<RestoreResult> => {
  if (!isNative) return { status: 'missing' };

  try {
    const stored = await SecureStorage.get(SESSION_KEY);
    storeFailure = null;

    if (stored === null || stored === undefined) {
      cachedSessionId = null;
      return { status: 'missing' };
    }
    if (typeof stored !== 'string' || stored.length === 0) {
      cachedSessionId = null;
      const error = new Error(`Stored session was ${typeof stored}, expected a non-empty string`);
      console.error('[NativeSession] Stored session is corrupt:', error.message);
      return { status: 'corrupt', error };
    }

    cachedSessionId = stored;
    return { status: 'restored', sessionId: stored };
  } catch (caught) {
    cachedSessionId = null;
    const error = toError(caught);
    const corrupt =
      caught instanceof StorageError && caught.code === StorageErrorType.invalidData;

    // Loud on purpose. The next missing plugin, signing problem or OS error must
    // announce itself rather than presenting as a logged-out user.
    console.error(
      `[NativeSession] Secure storage ${corrupt ? 'returned corrupt data' : 'is unavailable'}:`,
      error
    );

    if (corrupt) return { status: 'corrupt', error };
    storeFailure = error.message;
    return { status: 'unavailable', error };
  }
};

/**
 * Persist a freshly issued session id. No-op on web, which uses the cookie.
 *
 * Deliberately does not throw: the in-memory copy means the user IS signed in
 * for this run. But the failure is recorded and logged — losing persistence
 * silently is exactly the bug this file was rewritten to prevent.
 */
export const setSessionId = async (sessionId: string): Promise<void> => {
  if (!isNative) return;
  cachedSessionId = sessionId;

  try {
    await SecureStorage.set(SESSION_KEY, sessionId);
    storeFailure = null;
  } catch (caught) {
    const error = toError(caught);
    storeFailure = error.message;
    console.error('[NativeSession] Could not persist the session to secure storage:', error);
  }
};

/**
 * Forget the session — logout, or a server-confirmed dead session.
 *
 * This must actually succeed. iOS Keychain entries survive app uninstall, so a
 * session left behind can silently sign a later install back in as the previous
 * user.
 */
export const clearSessionId = async (): Promise<void> => {
  cachedSessionId = null;
  if (!isNative) return;

  try {
    await SecureStorage.remove(SESSION_KEY);
  } catch (caught) {
    console.error('[NativeSession] Could not clear the stored session:', toError(caught));
  }
};

/**
 * Capture the session id from a response, if the server issued one. Native-only
 * by construction: the backend emits `X-Session-Id` only when the request
 * carried `X-Client-Platform: native`.
 */
export const captureSessionId = async (response: Response): Promise<void> => {
  if (!isNative) return;
  const issued = response.headers.get('X-Session-Id');
  if (issued) await setSessionId(issued);
};
